import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  ButtonGroup,
  Spinner,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  getDashboardAnalytics,
  getRevenueTrends,
  exportDashboardAnalytics,
} from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class AnalyticsDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      period: "month",
      dashboardData: null,
      trendsData: null,
      breadcrumbItems: [
        { title: "Lubumbashi", link: "#" },
        { title: "Analytics", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadDashboardData();
  }

  loadDashboardData = async () => {
    this.setState({ loading: true });
    const { period } = this.state;

    const [dashboardResponse, trendsResponse] = await Promise.all([
      getDashboardAnalytics(period),
      getRevenueTrends(period),
    ]);

    this.setState({
      dashboardData: dashboardResponse?.success ? dashboardResponse.data : null,
      trendsData: trendsResponse?.success ? trendsResponse.data : null,
      loading: false,
    });
  };

  handlePeriodChange = (period) => {
    this.setState({ period }, this.loadDashboardData);
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const res = await exportDashboardAnalytics({});
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  renderGrowthIndicator = (growth) => {
    if (!growth && growth !== 0) return null;
    const isPositive = growth >= 0;
    return (
      <span className={`ml-2 ${isPositive ? "text-success" : "text-danger"}`}>
        <i className={`ri-arrow-${isPositive ? "up" : "down"}-line`}></i>
        {Math.abs(growth).toFixed(1)}%
      </span>
    );
  };

  render() {
    const { loading, period, dashboardData, trendsData } = this.state;

    if (loading) {
      return (
        <div className="page-content">
          <Container fluid>
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading analytics...</p>
            </div>
          </Container>
        </div>
      );
    }

    const kpis = dashboardData?.kpis || {};
    const revenueBreakdown = dashboardData?.revenueBreakdown || {};
    const growth = trendsData?.growth || {};

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Period Selector */}
            <Row className="mb-4">
              <Col>
                <ButtonGroup>
                  {["week", "month", "year"].map((p) => (
                    <Button
                      key={p}
                      color={period === p ? "primary" : "light"}
                      onClick={() => this.handlePeriodChange(p)}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </Button>
                  ))}
                </ButtonGroup>
                <Button color="success" className="ml-2" disabled={this.state.exporting} onClick={this.handleExport}>
                  {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                </Button>
              </Col>
            </Row>

            {/* KPI Cards */}
            <Row>
              <Col md={3}>
                <Card className="mini-stat bg-primary">
                  <CardBody className="text-white">
                    <div className="mb-4">
                      <div className="float-left mini-stat-img mr-4">
                        <i className="ri-money-dollar-circle-line" style={{ fontSize: "40px" }}></i>
                      </div>
                      <h5 className="font-size-16 text-uppercase mt-0 text-white-50">
                        Total Revenue
                      </h5>
                      <h4 className="font-weight-medium font-size-24">
                        {this.formatCurrency(kpis.totalRevenue)}
                        {this.renderGrowthIndicator(growth.total)}
                      </h4>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="mini-stat bg-success">
                  <CardBody className="text-white">
                    <div className="mb-4">
                      <div className="float-left mini-stat-img mr-4">
                        <i className="ri-restaurant-line" style={{ fontSize: "40px" }}></i>
                      </div>
                      <h5 className="font-size-16 text-uppercase mt-0 text-white-50">
                        Food Orders
                      </h5>
                      <h4 className="font-weight-medium font-size-24">
                        {this.formatNumber(kpis.foodOrders)}
                      </h4>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="mini-stat bg-warning">
                  <CardBody className="text-white">
                    <div className="mb-4">
                      <div className="float-left mini-stat-img mr-4">
                        <i className="ri-hotel-bed-line" style={{ fontSize: "40px" }}></i>
                      </div>
                      <h5 className="font-size-16 text-uppercase mt-0 text-white-50">
                        Room Bookings
                      </h5>
                      <h4 className="font-weight-medium font-size-24">
                        {this.formatNumber(kpis.roomBookings)}
                      </h4>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="mini-stat bg-info">
                  <CardBody className="text-white">
                    <div className="mb-4">
                      <div className="float-left mini-stat-img mr-4">
                        <i className="ri-car-line" style={{ fontSize: "40px" }}></i>
                      </div>
                      <h5 className="font-size-16 text-uppercase mt-0 text-white-50">
                        Transport Requests
                      </h5>
                      <h4 className="font-weight-medium font-size-24">
                        {this.formatNumber(kpis.transportRequests)}
                      </h4>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Revenue Breakdown */}
            <Row>
              <Col md={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Revenue Breakdown</h4>
                    <div className="table-responsive">
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td>
                              <i className="ri-restaurant-line text-success mr-2"></i>
                              Food
                            </td>
                            <td className="text-right">
                              <strong>{this.formatCurrency(revenueBreakdown.food)}</strong>
                              {this.renderGrowthIndicator(growth.food)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <i className="ri-hotel-bed-line text-warning mr-2"></i>
                              Rooms
                            </td>
                            <td className="text-right">
                              <strong>{this.formatCurrency(revenueBreakdown.room)}</strong>
                              {this.renderGrowthIndicator(growth.room)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <i className="ri-car-line text-info mr-2"></i>
                              Transport
                            </td>
                            <td className="text-right">
                              <strong>{this.formatCurrency(revenueBreakdown.transport)}</strong>
                              {this.renderGrowthIndicator(growth.transport)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3">
                      <Link to="/analytics/revenue" className="btn btn-primary btn-sm">
                        View Full Report
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Additional Metrics</h4>
                    <div className="table-responsive">
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td>
                              <i className="ri-table-line text-primary mr-2"></i>
                              Table Bookings
                            </td>
                            <td className="text-right">
                              <strong>{this.formatNumber(kpis.tableBookings)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <i className="ri-user-add-line text-success mr-2"></i>
                              New Users
                            </td>
                            <td className="text-right">
                              <strong>{this.formatNumber(kpis.newUsers)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Quick Links */}
            <Row>
              <Col md={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Detailed Analytics</h4>
                    <Row>
                      <Col md={2}>
                        <Link
                          to="/analytics/revenue"
                          className="btn btn-outline-primary btn-block mb-2"
                        >
                          <i className="ri-money-dollar-circle-line mr-2"></i>
                          Revenue
                        </Link>
                      </Col>
                      <Col md={2}>
                        <Link
                          to="/analytics/food"
                          className="btn btn-outline-success btn-block mb-2"
                        >
                          <i className="ri-restaurant-line mr-2"></i>
                          Food
                        </Link>
                      </Col>
                      <Col md={2}>
                        <Link
                          to="/analytics/room"
                          className="btn btn-outline-warning btn-block mb-2"
                        >
                          <i className="ri-hotel-bed-line mr-2"></i>
                          Rooms
                        </Link>
                      </Col>
                      <Col md={2}>
                        <Link
                          to="/analytics/table"
                          className="btn btn-outline-info btn-block mb-2"
                        >
                          <i className="ri-table-line mr-2"></i>
                          Tables
                        </Link>
                      </Col>
                      <Col md={2}>
                        <Link
                          to="/analytics/transport"
                          className="btn btn-outline-dark btn-block mb-2"
                        >
                          <i className="ri-car-line mr-2"></i>
                          Transport
                        </Link>
                      </Col>
                      <Col md={2}>
                        <Link
                          to="/analytics/users"
                          className="btn btn-outline-secondary btn-block mb-2"
                        >
                          <i className="ri-user-line mr-2"></i>
                          Users
                        </Link>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(AnalyticsDashboard));
